import { AppRoutes } from "@/constants/routes";
import {
  uploadTaxesFile,
  getTaxesUploadHistory,
  getLoadedTaxes,
  resendLoadedTax,
} from "@/services/taxes.service";
import { handlePermissionError } from "@/utils/permission-check";
import { Dispatch, SetStateAction } from "react";
import { NavigateFunction } from "react-router-dom";
import { toaster } from "@/components/ui/toaster";
import dayjs from "dayjs";
import { TaxesUploadStatus } from "@/constants/entity-status";
import {
  TaxUploadHistoryItem,
  TaxesUploadResult,
  GetLoadedTaxesBackendQuery,
  LoadedTaxesListResponse,
} from "./taxes.schema";
import { Option } from "@/components/select/AsyncAutocompleteSelect";

export type UploadUiStatus =
  | "idle"
  | "processing"
  | "success"
  | "invalid"
  | "recordErrors";

interface FetchTaxesUploadHistoryHandlerProps {
  setLoading: Dispatch<SetStateAction<boolean>>;
  setHistory: Dispatch<SetStateAction<TaxUploadHistoryItem[]>>;
  navigate: NavigateFunction;
  fetchPermissions?: () => Promise<void>;
  showLoad?: boolean;
}

export const fetchTaxesUploadHistoryHandler = async ({
  setLoading,
  setHistory,
  navigate,
  fetchPermissions,
  showLoad = true,
}: FetchTaxesUploadHistoryHandlerProps) => {
  try {
    if (showLoad) setLoading(true);
    const response = await getTaxesUploadHistory();
    setHistory(response ?? []);
  } catch (error) {
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.TAXES,
    );

    if (!handled) {
      toaster.create({
        type: "error",
        title: "No se pudo cargar el historial de cargas.",
      });
    }
  } finally {
    if (showLoad) setLoading(false);
  }
};

interface UploadTaxesFileHandlerProps {
  file: File;
  setUploadUiStatus: Dispatch<SetStateAction<UploadUiStatus>>;
  setUploadResultMessage: Dispatch<SetStateAction<string | null>>;
  setProcessingFileName: Dispatch<SetStateAction<string | null>>;
  setLastUploadedFileName: Dispatch<SetStateAction<string | null>>;
  setHistory: Dispatch<SetStateAction<TaxUploadHistoryItem[]>>;
}

export const uploadTaxesFileHandler = async ({
  file,
  setUploadUiStatus,
  setUploadResultMessage,
  setProcessingFileName,
  setLastUploadedFileName,
  setHistory,
}: UploadTaxesFileHandlerProps): Promise<TaxesUploadResult> => {
  setLastUploadedFileName(file.name);
  setProcessingFileName(file.name);
  setUploadUiStatus("processing");
  setUploadResultMessage(null);

  const nowItem: TaxUploadHistoryItem = {
    id: String(Date.now()),
    fechaCarga: dayjs().toISOString(),
    nombreArchivo: file.name,
    estado: TaxesUploadStatus.PROCESSING,
    detalle: "Validando estructura y datos",
  };

  setHistory((prev) => [nowItem, ...prev]);

  try {
    const { item, result } = await uploadTaxesFile({ file });

    setHistory((prev) => prev.map((h) => (h.id === nowItem.id ? item : h)));

    if (result.status === "invalid") {
      setUploadUiStatus("invalid");
      setUploadResultMessage(result.message);
    } else if (result.status === "recordErrors") {
      setUploadUiStatus("recordErrors");
      setUploadResultMessage(result.message);
    } else {
      setUploadUiStatus("success");
      setUploadResultMessage(result.message);
    }

    return result;
  } catch (error: any) {
    const message =
      String(error?.response?.data?.message ?? error?.message ?? "") ||
      "No se pudo procesar el archivo";

    setUploadUiStatus(
      message.toLowerCase().includes("fila(s) con errores")
        ? "recordErrors"
        : "invalid",
    );
    setUploadResultMessage(message);

    return {
      status: message.toLowerCase().includes("fila(s) con errores")
        ? "recordErrors"
        : "invalid",
      message,
    };
  } finally {
    setProcessingFileName(null);
  }
};

interface FetchLoadedTaxesHandlerProps {
  setLoading: Dispatch<SetStateAction<boolean>>;
  setItems: Dispatch<SetStateAction<LoadedTaxesListResponse | null>>;
  navigate: NavigateFunction;
  fetchPermissions?: () => Promise<void>;
  showLoad?: boolean;
  filters?: Partial<GetLoadedTaxesBackendQuery>;
}

export const fetchLoadedTaxesHandler = async ({
  setLoading,
  setItems,
  navigate,
  fetchPermissions,
  showLoad = true,
  filters,
}: FetchLoadedTaxesHandlerProps) => {
  try {
    if (showLoad) setLoading(true);

    const response = await getLoadedTaxes(filters);
    setItems(response);
  } catch (error) {
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.TAXES,
    );

    if (!handled) {
      toaster.create({
        type: "error",
        title: "No se pudo cargar el listado de impuestos.",
      });
    }
  } finally {
    if (showLoad) setLoading(false);
  }
};

export interface LoadedTaxesFiltersForm {
  fecha?: string;
  cuenta?: Option | null;
  tipoImpuesto?: (string | number)[];
  estado?: (string | number)[];
}

interface SubmitLoadedTaxesFiltersHandlerProps {
  event: LoadedTaxesFiltersForm;
  setOpenFilters: Dispatch<SetStateAction<boolean>>;
  setFiltersApplied: Dispatch<SetStateAction<boolean>>;
  setCurrentFilters: Dispatch<
    SetStateAction<Partial<GetLoadedTaxesBackendQuery> | undefined>
  >;
  fetchLoadedTaxes: (
    filters?: Partial<GetLoadedTaxesBackendQuery>,
    showLoad?: boolean,
  ) => Promise<void>;
}

export const submitLoadedTaxesFiltersHandler = async ({
  event,
  setOpenFilters,
  setFiltersApplied,
  setCurrentFilters,
  fetchLoadedTaxes,
}: SubmitLoadedTaxesFiltersHandlerProps) => {
  const cuentaValue = String((event?.cuenta as Option | null)?.value ?? "");

  const cuentaOrigen = cuentaValue ? Number(cuentaValue) : undefined;

  const tipoImpuestoArray = (Array.isArray(event?.tipoImpuesto) ? event.tipoImpuesto : [])
    .map((e: string | number) => Number(e))
    .filter((n: number) => Number.isFinite(n));
  const estadoArray = (Array.isArray(event?.estado) ? event.estado : [])
    .map((e: string | number) => Number(e))
    .filter((n: number) => Number.isFinite(n));

  const filterObj: Partial<GetLoadedTaxesBackendQuery> = {
    ...(event?.fecha ? { Fecha: event.fecha } : {}),
    ...(Number.isFinite(cuentaOrigen as number) ? { CuentaOrigen: cuentaOrigen } : {}),
    ...(tipoImpuestoArray.length > 0 ? { TipoImpuesto: tipoImpuestoArray } : {}),
    ...(estadoArray.length > 0 ? { Estados: estadoArray } : {}),
  };

  setCurrentFilters(filterObj);
  setFiltersApplied(Boolean(Object.keys(filterObj).length));
  await fetchLoadedTaxes({ ...filterObj, "P.Pagina": 1 }, true);
  setOpenFilters(false);
};

interface ClearLoadedTaxesFiltersHandlerProps {
  clear: () => void;
  setFiltersApplied: Dispatch<SetStateAction<boolean>>;
  setOpenFilters: Dispatch<SetStateAction<boolean>>;
  submitFilters: () => Promise<void>;
}

export const clearLoadedTaxesFiltersHandler = async ({
  clear,
  setFiltersApplied,
  setOpenFilters,
  submitFilters,
}: ClearLoadedTaxesFiltersHandlerProps) => {
  clear();
  await submitFilters();
  setFiltersApplied(false);
  setOpenFilters(false);
};



interface ResetUploadBoxProps {
  setProcessingFileName: Dispatch<SetStateAction<string | null>>;
  setLastUploadedFileName: Dispatch<SetStateAction<string | null>>;
  setUploadResultMessage: Dispatch<SetStateAction<string | null>>;
  setUploadUiStatus: Dispatch<SetStateAction<UploadUiStatus>>;
}

export const resetUploadBox = ({
  setProcessingFileName,
  setLastUploadedFileName,
  setUploadResultMessage,
  setUploadUiStatus,
}: ResetUploadBoxProps) => {
  setProcessingFileName(null);
  setLastUploadedFileName(null);
  setUploadResultMessage(null);
  setUploadUiStatus("idle");
};

export const validateFile = (file: File, maxFileSizeBytes: number) => {
  const lower = file.name.toLowerCase();
  if (!lower.endsWith(".csv")) {
    return "Solo se permiten archivos .csv";
  }

  if (file.size > maxFileSizeBytes) {
    return "El archivo supera el tamaño máximo permitido (10MB)";
  }

  return null;
};

interface ResendLoadedTaxHandlerProps {
  id: number;
  navigate: NavigateFunction;
  fetchPermissions?: () => Promise<void>;
}

export const resendLoadedTaxHandler = async ({
  id,
  navigate,
  fetchPermissions,
}: ResendLoadedTaxHandlerProps) => {
  try {
    const ok = await resendLoadedTax(id);
    if (!ok) {
      toaster.create({
        type: "error",
        title: "No se pudo reenviar el impuesto.",
      });
      return;
    }

    toaster.create({
      type: "success",
      title: "Impuesto reenviado.",
    });
  } catch (error) {
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.TAXES,
    );

    if (!handled) {
      toaster.create({
        type: "error",
        title: "No se pudo reenviar el impuesto.",
      });
    }
  }
};
